import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, Layers, ShieldCheck } from "lucide-react";
import CryptoPayModal from "./CryptoPayModal";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function AccessTiers() {
  const [catalog, setCatalog] = useState([]);
  const [referral, setReferral] = useState("");
  const [cryptoTier, setCryptoTier] = useState(null);

  // Pull the live tier catalog (11 scenario levels)
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/tiers/catalog`);
        const data = await r.json();
        setCatalog(Array.isArray(data?.tiers) ? data.tiers : []);
      } catch { /* keep UI quiet */ }
    })();
  }, []);

  // Referral code autofill
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = (params.get("ref") || "").trim().toUpperCase();
      if (raw && /^INV-[A-Z0-9]{4,}$/.test(raw)) setReferral(raw);
    } catch { /* URL API not available */ }
  }, []);

  // Mark the 4th level (Black) as featured — our most-chosen entry point
  const tiers = useMemo(
    () =>
      catalog.map((t, i) => ({
        ...t,
        featured: t.slug === "black" || (i === 3 && !catalog.some((x) => x.slug === "black")),
      })),
    [catalog],
  );

  return (
    <section
      id="access"
      data-testid="access-tiers"
      className="relative overflow-hidden bg-ink py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[10px] uppercase tracking-[0.35em] text-gold">
              Training scenarios · 11 levels
            </p>
            <h2 className="mt-3 max-w-2xl font-serif text-4xl font-light leading-tight text-white md:text-5xl">
              Pick a <span className="text-gold">scenario level</span> and
              practise on a wider book.
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/55">
              Each level unlocks a larger simulated book, deeper tooling and
              more concierge drills. All scenario mechanics are shown inside
              the terminal after you sign in — nothing is promised in advance.
            </p>
          </div>
          <div className="w-full max-w-xs">
            <label
              htmlFor="referral-input"
              className="text-[10px] uppercase tracking-[0.25em] text-white/40"
            >
              Referral code (optional)
            </label>
            <input
              id="referral-input"
              data-testid="referral-input"
              value={referral}
              onChange={(e) => setReferral(e.target.value.toUpperCase())}
              placeholder="INV-XXXXXX"
              className="mt-1.5 w-full border border-white/15 bg-surface px-4 py-2.5 font-tabular text-sm text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
            />
          </div>
        </div>

        <div
          data-testid="simulation-notice"
          className="mt-10 flex flex-wrap items-center gap-3 rounded-sm border border-gold/30 bg-gold/[0.05] px-5 py-3"
        >
          <ShieldCheck className="h-4 w-4 text-gold" />
          <span className="text-[11px] uppercase tracking-[0.22em] text-gold">
            Simulated terminal · training only
          </span>
          <span className="text-[11px] text-white/55">
            Scenario credits have no cash value. Full scenario mechanics —
            including any training payouts — are displayed inside the terminal
            after sign-in.
          </span>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {tiers.map((t, i) => (
            <motion.div
              key={t.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.04 }}
              data-testid={`access-tier-card-${t.slug}`}
              className={`relative flex flex-col justify-between overflow-hidden rounded-lg border p-6 transition-all duration-300 hover:-translate-y-1 ${
                t.featured
                  ? "border-gold bg-gradient-to-br from-gold/[0.06] to-transparent"
                  : "border-white/10 bg-surface"
              }`}
              style={{
                boxShadow: `inset 0 -2px 0 0 ${t.theme_hex}`,
              }}
            >
              {t.featured && (
                <span className="absolute right-4 top-4 rounded-full bg-gold px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-ink">
                  Most chosen
                </span>
              )}
              <div>
                <p
                  className="text-[10px] uppercase tracking-[0.28em]"
                  style={{ color: t.theme_hex }}
                >
                  {t.name}
                </p>
                <div className="mt-3 flex items-baseline gap-3">
                  <p className="font-serif text-5xl font-light text-white" data-testid={`tier-price-${t.slug}`}>
                    <span className="align-top text-lg text-gold">$</span>
                    {Number(t.price).toLocaleString("en-US")}
                  </p>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                    L{i + 1}
                  </span>
                </div>
                <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-gold/70">
                  One-time scenario access · USD
                </p>

                <div className="mt-5 space-y-2 text-[11px] text-white/75">
                  <div className="flex items-center justify-between border-b border-white/5 pb-1.5">
                    <span className="flex items-center gap-1.5 text-white/50">
                      <Layers className="h-3 w-3 text-gold" />
                      Simulated book
                    </span>
                    <span className="font-tabular text-gold">
                      Level {i + 1} depth
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-white/5 pb-1.5">
                    <span className="flex items-center gap-1.5 text-white/50">
                      <GraduationCap className="h-3 w-3 text-gold" />
                      Training tools
                    </span>
                    <span className="font-tabular text-white/90">
                      {i < 3 ? "Core" : i < 6 ? "Advanced" : i < 9 ? "Pro" : "Elite"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between border-b border-white/5 pb-1.5">
                    <span className="text-white/50">Concierge drills</span>
                    <span className="font-tabular text-white/90">
                      {i < 2 ? "Starter" : i < 5 ? "Standard" : i < 8 ? "Priority" : "Signature"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between pb-0.5">
                    <span className="text-white/50">Next level</span>
                    <span className="font-tabular text-white/90">
                      {t.next_tier_name || "—"}
                    </span>
                  </div>
                </div>
              </div>
              <button
                data-testid={`tier-crypto-btn-${t.slug}`}
                onClick={() =>
                  setCryptoTier({
                    key: `access_${t.price}`,
                    amount: t.price,
                    name: t.name,
                  })
                }
                className={`group mt-6 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                  t.featured
                    ? "bg-gold text-ink hover:bg-gold-light"
                    : "border border-gold/40 text-gold hover:bg-gold hover:text-ink"
                }`}
              >
                Unlock level
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-[10px] uppercase tracking-[0.25em] text-white/30">
          Simulated training terminal · scenario mechanics shown after sign-in
        </p>
        <p
          className="mt-3 max-w-xl text-[11px] leading-relaxed text-white/35"
          data-testid="refund-policy-note"
        >
          Scenario credits are a one-time usage right inside the simulation.
          Full scenario mechanics — including training payout cadence and
          cycle rules — are disclosed inside the terminal after sign-in. See
          our Terms and Risk Disclaimer for the full picture.
        </p>
      </div>

      <CryptoPayModal
        open={!!cryptoTier}
        onClose={() => setCryptoTier(null)}
        lookupKey={cryptoTier?.key}
        targetAmount={cryptoTier?.amount}
        referralCode={referral.trim().toUpperCase() || null}
      />
    </section>
  );
}
